import { useState } from 'react';

type Tab = 'hizli' | 'tam';

interface Step {
  title: string;
  points: string[];
  tip?: string;
}

const hizliSteps: Step[] = [
  {
    title: 'Hazırlık — Panele Giriş',
    points: [
      'Panelin adresini tarayıcıda aç, "Giriş Yap" sekmesinde sana verilen e-posta ve şifreyle giriş yap.',
      'Giriş başarılıysa otomatik olarak Dashboard (ana ekran) açılır.',
    ],
    tip: 'Şifreni ilk girişten sonra "Ayarlar" bölümünden değiştirebilirsin.',
  },
  {
    title: 'Seviyeleri Tanımla (Ayarlar > Seviye Ayarları)',
    points: [
      'Sol menüden "Ayarlar"a gir, üstteki sekmelerden "Seviye Ayarları"nı seç.',
      'Hazır seviyeler (4. Sınıf, TYT, AYT, KPSS, AGS vb.) zaten tanımlıdır.',
      'Yeni bir seviye eklemek için "Yeni Seviye Ekle"ye bas; ad, kategori, açıklama ve sırayı gir.',
      'Bir seviyeyi pasife alabilir veya düzenleyebilirsin. Tüm seviyeler tek buradan yönetilir.',
    ],
    tip: 'Seviyeler; Grup, Öğrenci, Ders, Doküman ve WhatsApp alanlarında tek kaynaktan gelir.',
  },
  {
    title: 'Bir Grup Oluştur',
    points: [
      'Sol menüden "Grup Yönetimi"ne tıkla, sağ üstteki "Yeni Grup Ekle" butonuna bas.',
      'Grup adı ve seviyeyi seç; ders günü, ders saati, ders günü notu ve renk gir.',
      'Varsa WhatsApp grup linkini de buraya yapıştır (öğrenci bu linki kendi portalında görür).',
      '"Aktif" durumunun seçili olduğundan emin ol ve "Grubu Kaydet"e bas.',
    ],
    tip: 'Dersler her zaman bir gruba bağlıdır; bu yüzden önce grup kurulur.',
  },
  {
    title: 'Öğrenci Ekle',
    points: [
      'Sol menüden "Öğrenciler"e tıkla, "Yeni Öğrenci Ekle" butonuna bas.',
      'Ad soyad, e-posta, telefon, seviye ve grubu gir (grup seçimi zorunludur).',
      '"Öğrenciyi Ekle"ye bas. Öğrenci hesabı otomatik oluşturulur.',
    ],
    tip: 'Öğrencinin e-postasını ve şifresini mutlaka not al; öğrenci bunlarla kendi portalına girer.',
  },
  {
    title: 'Ders Ekle (Canlı Ders Linki Dahil)',
    points: [
      'Sol menüden "Dersler"e tıkla, "Yeni Ders Ekle" butonuna bas.',
      'Grubu seç (seviye otomatik gelir), ders başlığı, konu, tarih ve saat gir.',
      'En önemli alan: "Canlı Ders Linki". Zoom veya Google Meet toplantısını oluştur ve linki buraya yapıştır.',
      '"Dersi Kaydet"e bas. Ders takvimde ve listede görünür.',
    ],
    tip: 'Canlı ders linkini WhatsApp grubuna da yazarsan öğrenciler anında katılır.',
  },
  {
    title: 'Dersten Sonra (Kayıt, Doküman, Ödev)',
    points: [
      '"Ders Kayıtları"ndan video kaydının linkini ekle ve "Yayında" olarak işaretle.',
      '"Dokümanlar"dan derste kullandığın PDF\'leri yükle (ders seçince grup ve seviye otomatik gelir).',
      'Gerekirse "Ödevler"den ilgili gruba ödev gönder.',
    ],
    tip: 'Bunlar öğrencinin kendi portalında otomatik görünür.',
  },
];

interface Module {
  title: string;
  icon: string;
  desc: string;
  points: string[];
}

const teacherModules: Module[] = [
  {
    title: 'Dashboard',
    icon: 'ri-dashboard-3-line',
    desc: 'Girişte açılan ana ekran.',
    points: [
      'KPI kartları: toplam öğrenci, bugünkü ders, bekleyen ödev, toplam kayıt gibi özet sayılar.',
      'Bugünün dersleri, yaklaşan dersler, son kayıtlar ve bekleyen ödevler tek bakışta.',
      '"Tümünü Gör" bağlantıları seni ilgili bölüme götürür.',
    ],
  },
  {
    title: 'Ders Programı',
    icon: 'ri-calendar-2-line',
    desc: 'Derslerin takvim üzerinde görüldüğü yer.',
    points: [
      'Hafta / Liste görünümü arasında geçiş yapabilirsin.',
      'Sağ/sol oklarla haftalar arası gezin, "Bugün" ile bugüne dön.',
      'Grup filtresiyle belirli bir grubun derslerini gör. Derse tıklayınca detay açılır.',
    ],
  },
  {
    title: 'Dersler',
    icon: 'ri-book-open-line',
    desc: 'Tüm derslerin listesi ve yönetimi.',
    points: [
      'Ders adı, grup, seviye, tarih/saat ve durum görünür.',
      'Grup seçilince seviye otomatik bilgi olarak gelir.',
      'Yeşil "Canlı Ders" butonu dersin Zoom/Meet linkini açar.',
      '"Düzenle" ile bilgileri değiştir, arama ve durum filtresi kullan.',
    ],
  },
  {
    title: 'Ders Kayıtları',
    icon: 'ri-vidicon-line',
    desc: 'Derslerin video kayıtlarının yönetimi.',
    points: [
      '"Yeni Kayıt Ekle" ile bağlı dersi seç (grup otomatik gelir) ve video linkini/süresini gir.',
      '"Yayında" işaretli olan kayıtları öğrenci görür; "Taslak" olanlar gizli kalır.',
      'Her kaydın hangi derse/gruba bağlı olduğu ve izlenme sayısı görünür.',
    ],
  },
  {
    title: 'Dokümanlar',
    icon: 'ri-file-pdf-2-line',
    desc: 'PDF ve ders notlarının yüklendiği yer.',
    points: [
      '"Yeni Doküman Ekle" ile PDF yükle, başlık ve bağlı ders/grup gir.',
      'Ders seçince grup ve seviye otomatik gelir; ders seçilmezse grup seçimi zorunludur.',
      'İndirilebilir olup olmadığını seç.',
      'Öğrenci yalnızca kendi grubunun dokümanlarını görür ve indirir.',
    ],
  },
  {
    title: 'Ödevler',
    icon: 'ri-task-line',
    desc: 'Ödev oluşturma ve takip merkezi.',
    points: [
      'Ödev oluştur: başlık, açıklama, grup, ilgili ders, son teslim tarihi, zorunluluk, link/dosya.',
      '"Tüm gruba gönder" veya "Seçili öğrencilere gönder" seçebilirsin.',
      'Teslim durumu tablosunda kimin teslim ettiğini, kontrol edildiğini veya eksik olduğunu takip et.',
      'Öğrenci ödevi portalından teslim eder, sen burada durumu güncellersin.',
    ],
  },
  {
    title: 'Öğrenciler',
    icon: 'ri-user-star-line',
    desc: 'Tüm öğrencilerin kart görünümü.',
    points: [
      'Her kartta isim, seviye, grup, e-posta, telefon, veli bilgisi ve aktif/pasif durumu (yazılı).',
      '"Detay", "Düzenle", "Sil" ve "Aktife/Pasife Al" butonları kartta yer alır.',
      'Arama kutusuyla isim/e-posta/telefon/seviye ara.',
    ],
  },
  {
    title: 'Grup Yönetimi',
    icon: 'ri-team-line',
    desc: 'Öğrenci gruplarının (sınıfların) yönetimi.',
    points: [
      '"Yeni Grup Ekle" ile ad, seviye, gün, saat, ders günü notu, renk ve WhatsApp linki gir.',
      '"Grup Detayı"nda o gruba bağlı öğrenciler, dersler, kayıtlar, dokümanlar, ödevler ve duyurular görülür.',
      'Bir grubu pasife alabilirsin (dersler bitince); pasif grup öğrenci portalında gizlenir.',
    ],
  },
  {
    title: 'Duyurular',
    icon: 'ri-megaphone-line',
    desc: 'Öğrencilere duyuru gönderme.',
    points: [
      '"Yeni Duyuru" ile başlık, mesaj, hedef grup ve öncelik (Düşük/Orta/Yüksek) seç.',
      '"Yayında" yapılan duyuruları öğrenci kendi portalında görür.',
      'Öğrenci yalnızca kendi grubuna gönderilen duyuruları görür.',
    ],
  },
  {
    title: 'WhatsApp Grupları',
    icon: 'ri-whatsapp-line',
    desc: 'Grupların WhatsApp linklerinin görüldüğü ekran.',
    points: [
      'WhatsApp linki tek kaynaktan gelir: Grup Yönetimi içindeki grubun kendi linki.',
      'Bu sayfa, gruplarda tanımlı linkleri listeleyen yardımcı görünümdür.',
      '"Linki Kopyala" ile hızlı paylaş, "Linki Aç" ile gruba git.',
      'Öğrenci yalnızca kendi grubunun linkini görür; başka grubun linkini göremez.',
    ],
  },
  {
    title: 'Ödeme / Paket Takibi',
    icon: 'ri-wallet-3-line',
    desc: 'Öğrenci paket ve ödemelerinin takibi.',
    points: [
      'KPI kartları: Toplam Tahsilat, Bekleyen Tahsilat, Aktif Paket, Bitmek Üzere.',
      'Paket tipleri: Birebir Ders, Grup Dersi, Kamp, Deneme Dersi, Aylık Abonelik.',
      'Paket tablosu: her öğrencinin toplam/kullanılan/kalan seansı (ilerleme çubuğu ile), ödeme durumu, geçerlilik tarihi.',
      '"Bitmek Üzere" (kırmızı) = seansı biten öğrenci, yeni paket satma zamanı.',
    ],
  },
  {
    title: 'Raporlar',
    icon: 'ri-bar-chart-2-line',
    desc: 'Öğrenci başarı istatistikleri ve grafikler.',
    points: [
      'KPI kartları: toplam öğrenci, toplam ders, kayıt izlenme, ödev tamamlama oranı.',
      'Grafikler: seviye dağılımı, aylık ders sayısı, en çok izlenen kayıtlar, ödev teslim durumu.',
      'Öğrenci başarı özeti: performans, devam ve ödev tamamlama yüzdesi (renkli ilerleme çubuğu).',
    ],
  },
  {
    title: 'Ayarlar',
    icon: 'ri-settings-3-line',
    desc: 'Hesap ve eğitim yapısı ayarları.',
    points: [
      'Hesap: profil bilgileri (ad soyad, telefon) ve şifre değiştirme.',
      'Seviye Ayarları: seviye ekle, düzenle, pasife al ve sırala (tüm seviyeler tek kaynaktan).',
      'Deneme Dersi (Misafir Girişi): misafir deneme dersi ayarları.',
    ],
  },
];

interface StudentModule {
  name: string;
  desc: string;
}

const studentModules: StudentModule[] = [
  { name: 'Ana Sayfa', desc: 'Hoş geldin + sıradaki ders + son kayıt + ödev + duyuru özeti.' },
  { name: 'Ders Programım', desc: 'Kendi grubunun ders programı ve canlı ders linkleri.' },
  { name: 'Ders Kayıtlarım', desc: 'Kendi grubuna ait yayındaki ders kayıtları.' },
  { name: 'Dokümanlarım', desc: 'Kendi grubunun PDF dokümanları (indirilebilir).' },
  { name: 'Ödevlerim', desc: 'Kendi grubuna gelen ödevler + "Ödevi Teslim Et" butonu.' },
  { name: 'Duyurular', desc: 'Hocanın kendi grubuna gönderdiği duyurular.' },
  { name: 'WhatsApp Grubum', desc: 'Kendi grubunun WhatsApp linki (tek kaynaktan).' },
  { name: 'Profilim', desc: 'Kendi bilgilerini düzenle + şifre değiştir.' },
];

const dailyFlow: string[] = [
  'Giriş yap.',
  'Dashboard\'dan bugünkü dersleri kontrol et.',
  'Ders Programı\'ndan canlı ders linkini kontrol et.',
  'Ders sonrası "Ders Kayıtları" bölümünden kayıt linkini ekle.',
  '"Dokümanlar" bölümünden PDF yükle.',
  '"Ödevler" bölümünden ilgili gruba ödev gönder.',
  '"Grup Detayı"ndan o gruba ait öğrenci, ders, kayıt, doküman ve ödevleri kontrol et.',
  '"Ödeme / Paket" bölümünden seansı bitmek üzere olan öğrencileri takip et.',
];

const kurulumSirasi: string[] = [
  'Önce seviye oluşturulur (Ayarlar > Seviye Ayarları).',
  'Sonra grup oluşturulur ve bir seviyeye bağlanır (Grup Yönetimi).',
  'Öğrenci eklenir ve gruba bağlanır (Öğrenciler).',
  'Ders gruba açılır (Dersler).',
  'Kayıt, doküman, ödev ve duyuru gruba bağlanır.',
  'Öğrenci yalnızca kendi aktif grubuna ait içerikleri görür.',
  'WhatsApp linki Grup Yönetimi içindeki grubun kendi linkinden gelir.',
];

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg bg-accent-100/70 px-4 py-3 text-sm text-accent-900 ring-1 ring-accent-200">
      <i className="ri-lightbulb-line mt-0.5 text-base text-accent-600" />
      <span>{children}</span>
    </div>
  );
}

function HizliBaslangic() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-foreground-600">
        İlk canlı derse kadar geçen süreci hiç takılmadan tamamlamak için bu adımları sırasıyla takip et.
      </p>
      {hizliSteps.map((step, idx) => (
        <div key={step.title} className="rounded-xl border border-background-200 bg-white p-5">
          <div className="flex items-start gap-4">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-white">
              {idx + 1}
            </div>
            <div className="min-w-0 flex-1">
              <h4 className="font-heading text-base font-bold text-foreground-950">{step.title}</h4>
              <ul className="mt-3 space-y-2">
                {step.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-500" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              {step.tip && (
                <div className="mt-3">
                  <Callout>{step.tip}</Callout>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function TamKilavuz() {
  return (
    <div className="space-y-8">
      {/* Giriş ve kayıt */}
      <section>
        <h3 className="font-heading text-lg font-bold text-foreground-950">1. Giriş ve Kayıt Sistemi</h3>
        <p className="mt-1.5 text-sm text-foreground-600">Panel iki ayrı alana sahiptir:</p>
        <div className="mt-3 overflow-hidden rounded-xl border border-background-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/60 text-xs text-foreground-500">
                <th className="px-4 py-3 font-semibold">Rol</th>
                <th className="px-4 py-3 font-semibold">Girdiği Yer</th>
                <th className="px-4 py-3 font-semibold">Ne Yapar?</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-background-100">
                <td className="px-4 py-3 font-semibold text-foreground-900">Öğretmen (Zehra Hoca)</td>
                <td className="px-4 py-3 text-foreground-700">Öğretmen Paneli</td>
                <td className="px-4 py-3 text-foreground-600">Her şeyi yönetir: seviye, grup, öğrenci, ders, kayıt, doküman, ödev, duyuru, paket, rapor...</td>
              </tr>
              <tr>
                <td className="px-4 py-3 font-semibold text-foreground-900">Öğrenci</td>
                <td className="px-4 py-3 text-foreground-700">Öğrenci Portalı</td>
                <td className="px-4 py-3 text-foreground-600">Yalnızca kendi grubuna ait ders, kayıt, doküman, ödev ve duyuruları görür.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-background-200 bg-white p-5">
            <p className="font-semibold text-foreground-900">Öğretmen kaydı</p>
            <p className="mt-1 text-sm text-foreground-600">
              İlk kurulumda bir kez oluşturulur. Sana verilen e-posta ve şifreyle giriş yapılır.
            </p>
          </div>
          <div className="rounded-xl border border-background-200 bg-white p-5">
            <p className="font-semibold text-foreground-900">Öğrenci kaydı (2 yol)</p>
            <ul className="mt-2 space-y-2 text-sm text-foreground-700">
              <li className="flex items-start gap-2">
                <i className="ri-number-1 mt-0.5 text-primary-500" />
                <span><strong>Hoca ekler:</strong> Öğrenciler → Yeni Öğrenci Ekle → ad, e-posta, şifre, seviye, grup gir → sistem öğrencinin girişini otomatik oluşturur.</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-number-2 mt-0.5 text-primary-500" />
                <span><strong>Öğrenci kendisi kayıt olur:</strong> Giriş ekranında "Kayıt Ol" → ad, e-posta, telefon, seviye, şifre → e-postasına gelen onay linkine tıklar → giriş yapar.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Kurulum sırası */}
      <section>
        <h3 className="font-heading text-lg font-bold text-foreground-950">2. Kurulum Sırası (Önemli)</h3>
        <p className="mt-1.5 text-sm text-foreground-600">
          Panel şu zincirle çalışır. Sırayı takip edersen hiçbir adımda takılmazsın:
        </p>
        <div className="mt-4 rounded-xl border border-background-200 bg-white p-5">
          <ol className="space-y-3">
            {kurulumSirasi.map((step, idx) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">
                  {idx + 1}
                </span>
                <span className="text-sm text-foreground-700">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Öğretmen modülleri */}
      <section>
        <h3 className="font-heading text-lg font-bold text-foreground-950">3. Öğretmen Paneli Bölümleri</h3>
        <p className="mt-1.5 text-sm text-foreground-600">Soldaki menüde yer alan tüm bölümler ve ne işe yaradıkları:</p>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          {teacherModules.map((m) => (
            <div key={m.title} className="rounded-xl border border-background-200 bg-white p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className={`${m.icon} text-lg`} />
                </span>
                <h4 className="font-heading text-base font-bold text-foreground-950">{m.title}</h4>
              </div>
              <p className="mt-2 text-sm font-medium text-secondary-700">{m.desc}</p>
              <ul className="mt-3 space-y-1.5">
                {m.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-sm text-foreground-600">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Öğrenci portalı */}
      <section>
        <h3 className="font-heading text-lg font-bold text-foreground-950">4. Öğrenci Portalı Bölümleri</h3>
        <p className="mt-1.5 text-sm text-foreground-600">
          Öğrenci, verilen e-posta ve şifreyle giriş yaptığında kendi paneline ulaşır:
        </p>
        <div className="mt-4 overflow-hidden rounded-xl border border-background-200 bg-white">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-background-200 bg-background-100/60 text-xs text-foreground-500">
                <th className="px-4 py-3 font-semibold">Bölüm</th>
                <th className="px-4 py-3 font-semibold">Ne Gösterir?</th>
              </tr>
            </thead>
            <tbody>
              {studentModules.map((s) => (
                <tr key={s.name} className="border-b border-background-100 last:border-0">
                  <td className="px-4 py-3 font-semibold text-foreground-900">{s.name}</td>
                  <td className="px-4 py-3 text-foreground-600">{s.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4">
          <Callout>
            Öğrenci yalnızca <strong>kendi aktif grubuna</strong> ait içeriği görür; diğer grupların dersleri, kayıtları, dokümanları ve ödevleri ona görünmez.
          </Callout>
        </div>
      </section>

      {/* Pasif grup */}
      <section>
        <h3 className="font-heading text-lg font-bold text-foreground-950">5. Pasif Grup Ne İşe Yarar?</h3>
        <p className="mt-1.5 text-sm text-foreground-600">
          Bir grubu pasife aldığında:
        </p>
        <div className="mt-3 rounded-xl border border-background-200 bg-white p-5">
          <ul className="space-y-2 text-sm text-foreground-700">
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-500" />
              <span>Grup, öğretmen panelinde görünmeye devam eder (düzenlenebilir, tekrar aktife alınabilir).</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-500" />
              <span>Pasif grubun dersleri, kayıtları, dokümanları, ödevleri ve duyuruları öğrenci portalında <strong>gizlenir</strong>.</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-checkbox-circle-line mt-0.5 shrink-0 text-accent-500" />
              <span>Dersler bittiğinde veya bir dönem kapandığında grubu pasife almak idealdir.</span>
            </li>
          </ul>
        </div>
      </section>

      {/* Günlük akış */}
      <section>
        <h3 className="font-heading text-lg font-bold text-foreground-950">6. Öğretmen Günlük Kullanım Akışı</h3>
        <div className="mt-4 rounded-xl border border-background-200 bg-white p-5">
          <ol className="space-y-3">
            {dailyFlow.map((step, idx) => (
              <li key={step} className="flex items-start gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-xs font-bold text-secondary-800">
                  {idx + 1}
                </span>
                <span className="text-sm text-foreground-700">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
}

export default function Yardim() {
  const [tab, setTab] = useState<Tab>('hizli');

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Yardım ve Kılavuzlar</h1>
          <p className="mt-1 text-sm text-foreground-600">
            Paneli kullanırken takıldığın her an bu kılavuzlara göz atabilirsin.
          </p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 cursor-pointer print:hidden"
        >
          <i className="ri-printer-line text-base" />
          Yazdır / PDF İndir
        </button>
      </div>

      {/* Sekme seçici */}
      <div className="inline-flex items-center gap-1 rounded-full bg-background-100 p-1 print:hidden">
        <button
          type="button"
          onClick={() => setTab('hizli')}
          className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition cursor-pointer ${
            tab === 'hizli' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
          }`}
        >
          <i className="ri-rocket-2-line mr-1.5" /> Hızlı Başlangıç
        </button>
        <button
          type="button"
          onClick={() => setTab('tam')}
          className={`whitespace-nowrap rounded-full px-5 py-2 text-sm font-semibold transition cursor-pointer ${
            tab === 'tam' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
          }`}
        >
          <i className="ri-book-2-line mr-1.5" /> Tam Kılavuz
        </button>
      </div>

      {/* Ekran görünümü */}
      <div className="print:hidden">
        {tab === 'hizli' ? <HizliBaslangic /> : <TamKilavuz />}
      </div>

      {/* Baskı görünümü — iki kılavuz birlikte */}
      <div className="hidden print:block">
        <h2 className="font-heading text-xl font-bold text-foreground-950">Hızlı Başlangıç</h2>
        <HizliBaslangic />
        <section className="print:break-before-page">
          <h2 className="font-heading text-xl font-bold text-foreground-950">Tam Kılavuz</h2>
          <TamKilavuz />
        </section>
      </div>
    </div>
  );
}